import { addedInlineClass, contextLineCount, removedInlineClass } from "./constants";
import type { DiffComputationResult, DiffResult, InlineDecoration } from "./types";

/**
 * Find the minimum indentation (leading spaces) across all non-empty lines
 */
const findMinIndentation = (lines: string[]): number => {
  let minIndent = Infinity;
  for (const line of lines) {
    if (line.trim() === "") {
      continue;
    }
    const match = line.match(/^(\s*)/);
    if (match) {
      minIndent = Math.min(minIndent, match[1].length);
    }
  }
  return minIndent === Infinity ? 0 : minIndent;
};

/**
 * Dedent lines by removing the minimum common indentation from both sets of lines
 */
const dedentLines = (originalLines: string[], modifiedLines: string[]): [string[], string[]] => {
  // Find min indentation across BOTH sets of lines for consistent dedenting
  const allLines = [...originalLines, ...modifiedLines];
  const minIndent = findMinIndentation(allLines);
  if (minIndent === 0) {
    return [originalLines, modifiedLines];
  }
  return [originalLines.map((line) => line.slice(minIndent)), modifiedLines.map((line) => line.slice(minIndent))];
};

/**
 * Detect the indentation step (e.g., 2 or 4 spaces) by finding the GCD of all indentation levels
 */
const detectIndentStep = (lines: string[]): number => {
  const indents: number[] = [];
  for (const line of lines) {
    if (line.trim() === "") {
      continue;
    }
    const match = line.match(/^(\s*)/);
    if (match && match[1].length > 0) {
      indents.push(match[1].length);
    }
  }
  if (indents.length === 0) {
    return 1;
  }
  // Find GCD of all indentation levels
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  return indents.reduce((acc, val) => gcd(acc, val), indents[0]) || 1;
};

/**
 * Normalize indentation to 1 space per level.
 * Only normalizes leading whitespace, preserving content exactly as-is
 * to avoid altering meaningful whitespace (e.g., in string literals).
 */
const normalizeIndentation = (lines: string[], indentStep: number): string[] => {
  return lines.map((line) => {
    const match = line.match(/^(\s*)(.*)/);
    if (!match) {
      return line;
    }
    const [, leading, content] = match;
    // Calculate indentation level and use 1 space per level
    const level = Math.floor(leading.length / indentStep);
    const normalizedLeading = " ".repeat(level);
    return normalizedLeading + content;
  });
};

/**
 * Preprocess lines: dedent and normalize indentation
 */
const preprocessLines = (originalLines: string[], modifiedLines: string[]): [string[], string[]] => {
  const [dedentedOrig, dedentedMod] = dedentLines(originalLines, modifiedLines);
  // Detect indent step from both sets of lines
  const allDedented = [...dedentedOrig, ...dedentedMod];
  const indentStep = detectIndentStep(allDedented);
  return [normalizeIndentation(dedentedOrig, indentStep), normalizeIndentation(dedentedMod, indentStep)];
};

type LineDiffOp = "equal" | "insert" | "delete";
type LineDiffEntry = { op: LineDiffOp; origIdx: number; modIdx: number; line: string };

/**
 * Maximum line length for character-level diff.
 * For longer lines, we skip character-level diff to avoid O(m*n) memory/time cost.
 */
const MAX_CHAR_DIFF_LENGTH = 500;

/**
 * Compute character-level diff using a simple LCS-based approach.
 * For very long lines (e.g., minified code), returns a simple delete+insert
 * to avoid O(m*n) memory and time complexity.
 */
export const computeCharDiff = (oldStr: string, newStr: string): DiffResult => {
  const result: DiffResult = [];
  const m = oldStr.length;
  const n = newStr.length;

  // For very long lines, skip expensive LCS computation and just show
  // the entire old line as deleted and entire new line as inserted
  if (m > MAX_CHAR_DIFF_LENGTH || n > MAX_CHAR_DIFF_LENGTH) {
    if (oldStr.length > 0) {
      result.push(["delete", oldStr]);
    }
    if (newStr.length > 0) {
      result.push(["insert", newStr]);
    }
    return result;
  }

  // Use dynamic programming to find LCS
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldStr[i - 1] === newStr[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find the diff
  let i = m;
  let j = n;
  const ops: DiffResult = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldStr[i - 1] === newStr[j - 1]) {
      ops.unshift(["equal", oldStr[i - 1]]);
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.unshift(["insert", newStr[j - 1]]);
      j--;
    } else {
      ops.unshift(["delete", oldStr[i - 1]]);
      i--;
    }
  }

  // Merge consecutive operations of the same type
  for (const [op, char] of ops) {
    if (result.length > 0 && result[result.length - 1][0] === op) {
      result[result.length - 1][1] += char;
    } else {
      result.push([op, char]);
    }
  }

  return result;
};

/**
 * Compute line-level diff using LCS algorithm
 */
const computeLineDiff = (originalLines: string[], modifiedLines: string[]): LineDiffEntry[] => {
  const m = originalLines.length;
  const n = modifiedLines.length;

  // Build LCS table
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (originalLines[i - 1] === modifiedLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find operations
  const ops: LineDiffEntry[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && originalLines[i - 1] === modifiedLines[j - 1]) {
      ops.unshift({ op: "equal", origIdx: i - 1, modIdx: j - 1, line: originalLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.unshift({ op: "insert", origIdx: -1, modIdx: j - 1, line: modifiedLines[j - 1] });
      j--;
    } else {
      ops.unshift({ op: "delete", origIdx: i - 1, modIdx: -1, line: originalLines[i - 1] });
      i--;
    }
  }

  return ops;
};

type ProcessedEntry =
  | { type: "equal"; origIdx: number; modIdx: number; line: string }
  | { type: "delete"; origIdx: number; line: string }
  | { type: "insert"; modIdx: number; line: string }
  | { type: "modify"; origIdx: number; modIdx: number; origLine: string; modLine: string };

/**
 * Convert raw line diff operations into processed entries,
 * merging adjacent delete+insert pairs into "modify" entries
 */
const processLineDiff = (lineDiff: LineDiffEntry[]): ProcessedEntry[] => {
  const result: ProcessedEntry[] = [];
  let i = 0;

  while (i < lineDiff.length) {
    const entry = lineDiff[i];

    if (entry.op === "equal") {
      result.push({ type: "equal", origIdx: entry.origIdx, modIdx: entry.modIdx, line: entry.line });
      i++;
    } else if (entry.op === "delete") {
      // Look ahead for an insert to pair with this delete
      const next = lineDiff[i + 1];
      if (next && next.op === "insert") {
        // This is a modification (delete + insert)
        result.push({
          type: "modify",
          origIdx: entry.origIdx,
          modIdx: next.modIdx,
          origLine: entry.line,
          modLine: next.line,
        });
        i += 2;
      } else {
        // Pure delete
        result.push({ type: "delete", origIdx: entry.origIdx, line: entry.line });
        i++;
      }
    } else if (entry.op === "insert") {
      // Pure insert (not paired with a preceding delete)
      result.push({ type: "insert", modIdx: entry.modIdx, line: entry.line });
      i++;
    } else {
      i++;
    }
  }

  return result;
};

/**
 * Compute diff, filter unchanged lines (with context), and create inline decorations
 */
export const computeDiff = (originalLines: string[], modifiedLines: string[]): DiffComputationResult => {
  const originalDecorations: InlineDecoration[] = [];
  const modifiedDecorations: InlineDecoration[] = [];
  const filteredOriginalLines: string[] = [];
  const filteredModifiedLines: string[] = [];
  const originalLineNumbers: number[] = [];
  const modifiedLineNumbers: number[] = [];

  // Preprocess lines: dedent and collapse spaces for cleaner display
  const [processedOriginal, processedModified] = preprocessLines(originalLines, modifiedLines);

  // Get line-level diff operations and process them
  const lineDiff = computeLineDiff(processedOriginal, processedModified);
  const processedDiff = processLineDiff(lineDiff);

  // Find indices of changed entries
  const changedEntryIndices = new Set<number>();
  processedDiff.forEach((entry, idx) => {
    if (entry.type !== "equal") {
      changedEntryIndices.add(idx);
    }
  });

  // Expand to include context
  const entriesToInclude = new Set<number>();
  for (const changedIdx of changedEntryIndices) {
    const start = Math.max(0, changedIdx - contextLineCount);
    const end = Math.min(processedDiff.length - 1, changedIdx + contextLineCount);
    for (let i = start; i <= end; i++) {
      entriesToInclude.add(i);
    }
  }

  // Sort indices
  const sortedEntryIndices = Array.from(entriesToInclude).sort((a, b) => a - b);

  // Build filtered content and decorations
  for (const entryIdx of sortedEntryIndices) {
    const entry = processedDiff[entryIdx];

    if (entry.type === "equal") {
      // Unchanged line - show in both editors
      filteredOriginalLines.push(entry.line);
      filteredModifiedLines.push(entry.line);
      originalLineNumbers.push(entry.origIdx + 1);
      modifiedLineNumbers.push(entry.modIdx + 1);
    } else if (entry.type === "delete") {
      // Deleted line - show in original only
      filteredOriginalLines.push(entry.line);
      originalLineNumbers.push(entry.origIdx + 1);

      // Highlight entire line as removed
      originalDecorations.push({
        lineNumber: filteredOriginalLines.length,
        startColumn: 1,
        endColumn: entry.line.length + 1,
        className: removedInlineClass,
      });
    } else if (entry.type === "insert") {
      // Inserted line - show in modified only
      filteredModifiedLines.push(entry.line);
      modifiedLineNumbers.push(entry.modIdx + 1);

      // Highlight entire line as added
      modifiedDecorations.push({
        lineNumber: filteredModifiedLines.length,
        startColumn: 1,
        endColumn: entry.line.length + 1,
        className: addedInlineClass,
      });
    } else if (entry.type === "modify") {
      // Modified line - show both versions with inline character diff
      filteredOriginalLines.push(entry.origLine);
      filteredModifiedLines.push(entry.modLine);
      originalLineNumbers.push(entry.origIdx + 1);
      modifiedLineNumbers.push(entry.modIdx + 1);

      const origLineNum = filteredOriginalLines.length;
      const modLineNum = filteredModifiedLines.length;

      // Compute character-level diff and apply decorations
      const charDiff = computeCharDiff(entry.origLine, entry.modLine);
      let origCol = 1;
      let modCol = 1;

      for (const [op, text] of charDiff) {
        const len = text.length;

        if (op === "delete") {
          originalDecorations.push({
            lineNumber: origLineNum,
            startColumn: origCol,
            endColumn: origCol + len,
            className: removedInlineClass,
          });
          origCol += len;
        } else if (op === "insert") {
          modifiedDecorations.push({
            lineNumber: modLineNum,
            startColumn: modCol,
            endColumn: modCol + len,
            className: addedInlineClass,
          });
          modCol += len;
        } else {
          origCol += len;
          modCol += len;
        }
      }
    }
  }

  // Final dedent: remove common leading indentation from filtered lines
  // and adjust decoration column positions accordingly
  const allFilteredLines = [...filteredOriginalLines, ...filteredModifiedLines].filter((l) => l !== "");
  const finalMinIndent = findMinIndentation(allFilteredLines);

  if (finalMinIndent > 0) {
    // Dedent all filtered lines
    for (let i = 0; i < filteredOriginalLines.length; i++) {
      if (filteredOriginalLines[i].length >= finalMinIndent) {
        filteredOriginalLines[i] = filteredOriginalLines[i].slice(finalMinIndent);
      }
    }
    for (let i = 0; i < filteredModifiedLines.length; i++) {
      if (filteredModifiedLines[i].length >= finalMinIndent) {
        filteredModifiedLines[i] = filteredModifiedLines[i].slice(finalMinIndent);
      }
    }

    // Adjust decoration column positions, filtering out decorations
    // that fall entirely within the dedented region
    const adjustDecorations = (decorations: InlineDecoration[]): InlineDecoration[] => {
      return decorations.filter((dec) => {
        // If the entire decoration is within the dedented region, remove it
        if (dec.endColumn <= finalMinIndent + 1) {
          return false;
        }
        // Adjust column positions
        dec.startColumn = Math.max(1, dec.startColumn - finalMinIndent);
        dec.endColumn = dec.endColumn - finalMinIndent;
        return true;
      });
    };

    const adjustedOriginal = adjustDecorations(originalDecorations);
    const adjustedModified = adjustDecorations(modifiedDecorations);
    originalDecorations.length = 0;
    originalDecorations.push(...adjustedOriginal);
    modifiedDecorations.length = 0;
    modifiedDecorations.push(...adjustedModified);
  }

  return {
    filteredOriginal: filteredOriginalLines.join("\n"),
    filteredModified: filteredModifiedLines.join("\n"),
    originalDecorations,
    modifiedDecorations,
    originalLineNumbers,
    modifiedLineNumbers,
  };
};
